/* eslint-disable react/jsx-props-no-spreading */
import ReactDOM from 'react-dom';
// import * as ReactDOMClient from 'react-dom/client';
// import { createRoot } from 'react-dom/client';
import Switch from '2-core-elements/Switch/Switch';
import ImageCard from '3-components/ImageCard/ImageCard';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { getDefaultFilters, getFilterQuery, getQueryURL, getWhereValue, type WhereItem } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import type { ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import useListingScroll from '3-components/ListingApp/useListingScroll';
import type { RealEstateMapItem } from '3-components/ListingMap/MapTypes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useRealEstateListingAllQuery, useRealEstateListingQuery, type RealEstateListingQuery } from 'cms/cms';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import { resolveRealEstateListing } from 'cms/resolvers/realEstateListingResolver';
import HTMLReactParser from 'html-react-parser';
import { useCallback, useContext, useEffect, useRef } from 'react';
import ListingMap, { type MapProps } from '../../3-components/ListingMap/ListingMap';
import { filterItemsById } from '../../3-components/ListingMap/ListingMapHelpers';
import MapCard from '../../3-components/ListingMap/MapCard';
import MapPanel from '../../3-components/ListingMap/MapPanel';
import { VIEW_LISTING, VIEW_MAP } from '../AssetsListing/AssetsListingContext';
import RealEstateListingContext, { type RealEstateListingContextProps, type RealEstateListingProps } from './RealEstateListingContext';
import RealEstateListingProvider from './RealEstateListingProvider';

// Australia focused bounds with padding
// Base approximate extreme points of mainland + TAS
const AU_BASE_BOUNDS = { west: 102.0, south: -44.0, east: 154.5, north: -9.0 };
// Add ~35 degrees each side (~70 degrees total span padding)
const AU_PADDING_DEG = 40;
const AUSTRALIA_BOUNDS = {
    west: AU_BASE_BOUNDS.west - AU_PADDING_DEG,
    south: AU_BASE_BOUNDS.south - AU_PADDING_DEG,
    east: AU_BASE_BOUNDS.east + AU_PADDING_DEG,
    north: AU_BASE_BOUNDS.north + AU_PADDING_DEG,
};
const DEFAULT_MAP_BOUNDS = [
    AUSTRALIA_BOUNDS.west,
    AUSTRALIA_BOUNDS.south,
    AUSTRALIA_BOUNDS.east,
    AUSTRALIA_BOUNDS.north
];

const listingMapSettings: MapProps<RealEstateMapItem>['settings'] = {
    defaultMapBounds: DEFAULT_MAP_BOUNDS,
    minZoom: 1.9,
    desktopMinZoom: 4,
    maxZoom: 27,
    defaultMapCenter: { lat: -25.0, lng: 134.0 },
    restriction: {
        latLngBounds: AUSTRALIA_BOUNDS,
        strictBounds: true,
    },
    clusterRadius: 80,
    iconSet: 'property',
};


const RealEstateListing = (): JSX.Element => {
    const {
        locale,
        map,
        view,
        setView,
        label,
        activeItemId,
        setActiveItemId,
        listing,
        setActiveFilters,
        activeFilters,
        filterModalOpen,
        setFilterModalOpen,
        cms,
        page,
        setPage,
    } = useContext(RealEstateListingContext) as RealEstateListingContextProps;

    const resetMapZoomRef = useRef<(() => void) | null>(null);

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const activeViewSetting = view === VIEW_MAP ? map : listing;
    const { filterByAncestors } = activeViewSetting;
    const { search, dateacquired, ...restParams } = getFilterQuery(activeFilters, activeViewSetting.filters);

    const whereItems: WhereItem[] = [];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (dateacquired) {
        whereItems.push({
            type: 'year',
            field: 'DateAcquired',
            value: dateacquired,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    const where = getWhereValue(whereItems);

    const isMapView = view === VIEW_MAP;

    // For listing view, use normal pagination
    const listingQuery = useRealEstateListingQuery({
        endpoint: `${OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale,
        where,
        skip: page * listing.itemsPerPage,
        limit: listing.itemsPerPage,
        ...restParams,
    }, {
        queryKey: ['real-estate-listing', page, activeFilters],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        enabled: !isMapView,
        select: useCallback((result: RealEstateListingQuery) => {
            return resolveRealEstateListing(result);
        }, []),
    });

    // For map view, fetch ALL data using the new hook
    const mapQuery = useRealEstateListingAllQuery({
        endpoint: `${OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale,
        where,
        ...restParams,
    }, {
        enabled: isMapView,
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: RealEstateListingQuery) => {
            return resolveRealEstateListing(result);
        }, []),
    });

    // Use appropriate query result based on view
    const { data, error, isLoading, isError, isFetching } = isMapView ? mapQuery : listingQuery;

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, activeViewSetting.filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    const scrollToEl = useListingScroll(page, isFetching);

    useEffect(() => {
        const restQuery = view === VIEW_MAP ? { view: undefined } : { view };
        window.history.replaceState({}, '', getQueryURL([], [], restQuery));
    }, [view]);

    // Only show loader on initial load, not when data exists and we're just fetching new data
    if (isLoading && !data) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    const showInactiveItems = !!map.showInactiveItems || true; // real estate set to true by defaultS

    // If user is not performing search then just show 1 activeItemId if there's activeItemId
    // Otherwise if user perform search or nearby location show all from search results
    const mapItems = (activeItemId && !showInactiveItems) ?
        filterItemsById(data.mapItems, activeItemId) as RealEstateMapItem[] :
        data.mapItems;

    return (
        <div className={`assets-listing-container container ${activeItemId ? 'pin-selected' : ''}`} ref={scrollToEl}>
            <div className="assets-listing-header">
                <h2 className="assets-listing-heading">{label.title}</h2>
                <div className="assets-listing-utility">
                    <Switch
                        size="lg"
                        offLabel={label.mapViewSwitchTitle}
                        onLabel={label.listViewSwitchTitle}
                        checked={view === VIEW_LISTING}
                        id="assets-listing-switch"
                        ariaLabel={`${label.mapViewSwitchTitle} / ${label.listViewSwitchTitle}`}
                        onChange={(nextChecked: boolean): void => {
                            const nextView = nextChecked ? VIEW_LISTING : VIEW_MAP;
                            const activeNextViewFilters = nextView === VIEW_LISTING ? listing.filters : map.filters;

                            // remove all unnecesary filters on query URL
                            window.history.replaceState({}, '', getQueryURL(getDefaultFilters(activeViewSetting.filters || []), activeViewSetting.filters || []));

                            // set active filters for cleaned version of it
                            updateFilters(getDefaultFilters(activeNextViewFilters || []));

                            // clear the active item id
                            setActiveItemId('');

                            // then change the view
                            setView(nextView);
                        }}
                    />
                </div>
            </div>
            {label.filterHeading && activeViewSetting.filters && (
                <ListingHeader
                    filters={activeViewSetting.filters}
                    activeFilters={activeFilters}
                    setActiveFilters={updateFilters}
                    title={label.filterHeading}
                    clearFilterLabel={label.clearFilterButton}
                    filterModalOpen={filterModalOpen}
                    setFilterModalOpen={setFilterModalOpen}
                    applyFilterLabel={label.applyFilterButton}
                    filterModalButtonLabel={label.filterModalButton}
                    filterModalHeadingLabel={label.filterModalHeading}
                    resetMapZoomRef={resetMapZoomRef}

                />
            )}
            <div className="assets-listing-content">
                {view === VIEW_MAP && (
                    <div className="map-layout">
                        <div className="map-column">
                            <ListingMap
                                apiKey={map.apiKey}
                                mapId={map.mapId}
                                items={mapItems}
                                settings={listingMapSettings}
                                activeItemId={activeItemId}
                                setActiveItemId={setActiveItemId}
                                resetZoomOnItemChange={false}
                                showInactiveItems={showInactiveItems}
                                showLoader={isFetching}
                                resetMapZoomRef={resetMapZoomRef}
                            />
                        </div>
                        <div className="map-info-column">
                            <div className="map-info-column-content">
                                <MapPanel isActive={!!activeItemId}>
                                    {mapItems.filter(item => {
                                        return item.id === activeItemId;
                                    }).map(({ name, id, img, description, sector, grade, state, status, href, icon }) => {
                                        // const countryNames = countries.map(country => {
                                        //     return country.name;
                                        // });
                                        return (
                                            <MapCard
                                                key={id}
                                                img={img}
                                                title={name}
                                                icon={icon}
                                                onCloseButtonClick={(): void => {
                                                    setActiveItemId('');
                                                }}
                                                infoItems={[
                                                    {
                                                        label: label.sectorLabel,
                                                        value: sector ?? '',
                                                    },
                                                    {
                                                        label: label.stateLabel,
                                                        value: state ?? '',
                                                    },
                                                    {
                                                        label: label.gradeLabel,
                                                        value: grade ?? '',
                                                    },
                                                    {
                                                        label: label.statusLabel,
                                                        value: status ?? '',
                                                    }
                                                ]}
                                                link={{
                                                    label: label.readMoreCardLabel,
                                                    href: href ?? '',
                                                }}
                                            >
                                                {HTMLReactParser(description)}
                                            </MapCard>
                                        );
                                    })}
                                </MapPanel>
                            </div>
                        </div>
                    </div>
                )}
                {view === VIEW_LISTING && (
                    <ListingContent
                        isFetching={isFetching}
                        isNoItems={!data.cardItems.length}
                        pagination={{
                            itemsPerPage: listing.itemsPerPage,
                            page,
                            setPage,
                            totalItems: data.total,
                        }}
                        loadingLabel={label.loadingMessage}
                        noItemsLabel={label.noItemMessage}
                        cols="3"
                    >
                        {data.cardItems.map((props, i) => {
                            const key = `${props.title}-${i}`;
                            return (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                                <ImageCard key={key} {...props} size="md" />
                                // <PropertyCard key={key} {...props} size="md" />
                            );
                        })}
                    </ListingContent>
                )}
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface RealEstateListingInitProps {
    el: HTMLDivElement;
    props: RealEstateListingProps;
}

const RealEstateListingInit = ({ el, props }: RealEstateListingInitProps): void => {
    // Using ReactDOM forced it to use React 17,
    // The plugin have issue with React 18, flickering on the pin
    ReactDOM.render(
        <QueryClientProvider client={queryClient}>
            <RealEstateListingProvider {...props}>
                <RealEstateListing />
            </RealEstateListingProvider>
        </QueryClientProvider>,
        el
    );
};

export default RealEstateListingInit;
